import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Lock, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { CartLine } from "@/components/cart/CartLine";
import { useCart } from "@/context/CartContext";
import { useStoreConfig } from "@/hooks/useProducts";
import { getLenis } from "@/hooks/useLenis";
import { api } from "@/lib/api";
import { formatApiError, formatEUR } from "@/lib/format";

const ShippingProgress = ({ subtotal, threshold }) => {
  const pct = Math.min(100, (subtotal / threshold) * 100);
  const missing = Math.max(0, threshold - subtotal);
  return (
    <div className="px-6 pt-5" data-testid="cart-shipping-progress">
      <p className="text-[15px] text-navy">{missing > 0 ? <>Ti mancano <strong>{formatEUR(missing)}</strong> per la spedizione gratuita</> : <>Hai diritto alla <strong>spedizione gratuita</strong></>}</p>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-sand">
        <div className="h-full rounded-full bg-copper transition-[width] duration-700" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const EmptyCart = ({ onClose }) => (
  <div className="flex flex-1 flex-col items-center justify-center px-8 text-center" data-testid="cart-empty">
    <span className="grid h-20 w-20 place-items-center rounded-full border border-copper/30 text-copper">
      <ShoppingBag className="h-8 w-8" strokeWidth={1.3} />
    </span>
    <p className="mt-6 font-display text-3xl text-navy">Il carrello è vuoto</p>
    <p className="mt-2 text-slate2">Scopri i dieci gesti di cura della linea Perfero.</p>
    <Link to="/collezione" onClick={onClose} className="btn-primary mt-8" data-testid="cart-empty-shop-link">
      Vai alla collezione
    </Link>
  </div>
);

export const CartDrawer = () => {
  const { items, open, setOpen, subtotal, count } = useCart();
  const { threshold, shippingCost } = useStoreConfig();
  const [loading, setLoading] = useState(false);
  const shipping = subtotal >= threshold ? 0 : shippingCost;

  useEffect(() => {
    const lenis = getLenis();
    if (!lenis) return undefined;
    if (open) lenis.stop();
    else lenis.start();
    return undefined;
  }, [open]);

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ slug: i.slug, quantity: i.quantity })),
        origin_url: window.location.origin,
      });
      if (!data?.checkout_url) throw new Error("missing url");
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" data-lenis-prevent data-testid="cart-drawer" className="flex w-full flex-col gap-0 border-l-copper/20 bg-cream p-0 sm:max-w-[480px]">
        <SheetHeader className="border-b border-copper/20 px-6 py-6 text-left">
          <SheetTitle className="font-display text-3xl font-medium text-navy">Il tuo carrello</SheetTitle>
          <SheetDescription className="text-[15px] text-slate2">{count} articoli · Acquisto come ospite</SheetDescription>
        </SheetHeader>
        {items.length === 0 ? (
          <EmptyCart onClose={() => setOpen(false)} />
        ) : (
          <>
            <ShippingProgress subtotal={subtotal} threshold={threshold} />
            <ul className="flex-1 divide-y divide-copper/15 overflow-y-auto px-6">
              {items.map((item) => (
                <CartLine key={item.slug} item={item} />
              ))}
            </ul>
            <div className="border-t border-copper/20 bg-ivory px-6 py-6">
              <dl className="space-y-2 text-[16px]">
                <div className="flex justify-between text-slate2"><dt>Subtotale</dt><dd data-testid="cart-subtotal">{formatEUR(subtotal)}</dd></div>
                <div className="flex justify-between text-slate2"><dt>Spedizione</dt><dd data-testid="cart-shipping">{shipping === 0 ? "Gratuita" : formatEUR(shipping)}</dd></div>
                <div className="flex justify-between pt-2 font-display text-2xl text-navy"><dt>Totale</dt><dd data-testid="cart-total">{formatEUR(subtotal + shipping)}</dd></div>
              </dl>
              <p className="mt-1 text-[13px] text-slate2">IVA inclusa</p>
              <button type="button" onClick={checkout} disabled={loading} data-testid="checkout-button" className="btn-primary mt-5 w-full text-lg">
                {loading ? "Ti stiamo indirizzando…" : "Procedi al pagamento sicuro"}
                {!loading && <ArrowRight className="h-5 w-5" />}
              </button>
              <p className="mt-3 flex items-center justify-center gap-2 text-[13px] text-slate2">
                <Lock className="h-3.5 w-3.5" /> Pagamento protetto da Stripe
              </p>
              <p className="mt-3 text-center text-[13px] leading-relaxed text-slate2" data-testid="cart-terms-note">
                Procedendo accetti i{" "}
                <Link to="/termini" onClick={() => setOpen(false)} className="text-navy underline underline-offset-2" data-testid="cart-terms-link">Termini e condizioni</Link>{" "}
                e confermi di aver letto la{" "}
                <Link to="/privacy" onClick={() => setOpen(false)} className="text-navy underline underline-offset-2" data-testid="cart-privacy-link">Privacy policy</Link>.
              </p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
